// janus-engine/actions/weapon-attack-commit.ts — A2 S3: the COMMIT ENVELOPE for one targeted attack (PURE).
//
// The resolver (weapon-attack.ts) decides; this module turns its outcome into the third client-truth
// schema, `janus.action.attack.v2`, and the frame every browser applies to BOTH actor rows. PHP records
// what is here — it never re-resolves the attack (owner Q1-REVISED: the browser rolls, the server records).
//
// ⭐ OWNER DECISION (2026-09-08): the commit carries the DRAWN FACES. `draws` is the ordered stream the
// recording source captured (die size + face, or a fraction), and the two log rows carry `rolls_full`
// tuples built to the SAME shape PHP writes today (roll_dice_arr + skill_roll_arr_identifiers for the
// attack; damage_type_roll + build_damage_dice_meta for the damage) — so the combat log renders
// identically (adv/dis highlighting reads `kept` / `roll_order`) and a later audit has the faces to check.
//
// Two-actor commit (owner Q4 + the draw-stream consult): the ATTACKER's revision guards the write; the
// TARGET's revision AND its `reaction` / `v_current` are PRECONDITIONS (a legacy reaction spend bumps no
// revision, so the revision alone cannot prove the target still had its reaction). ⚠ Bumping the
// target's revision is NEW behaviour the port introduces — legacy moves `movement_rev` on the attacker
// only (S1 golden §3).
import type { RecordedDraw } from './roll-source.js';
import { applyRawDiceModifiers } from '../raw-dice-modifiers.js';
import { CRIT_CASCADE_RULES, type CritCascadeTrigger } from './weapon-attack-cascades.js';
import { isFortunateStatus } from './weapon-attack-fortunate.js';
import type { AbilityUseVerdict } from './ability-use.js';
import { mergeAttackStatuses, type AttackStatusState } from './attack-status-projection.js';
import type { MaimedSave } from './weapon-attack-maimed.js';
import { maimedAscensionEffects } from './weapon-attack-maimed.js';
import { WEAPON_ATTACK_SCHEMA, EVADE_ID, WEAPON_MELEE_ATTACK_ROLL, type WeaponAttackDeltas, type WeaponAttackOutcome, type RiderSwingOutcome, type MitigationKind, type MitigationStep, type ExtraDamageFragment, type DamageAdjustment, type DamageFeedback } from './weapon-attack.js';
import { applyApsScalar, applyMovementTruth, type MovementTruthChange, type MovementTruthRow } from '../movement/client-truth-commit.js';
import { setRowAbilityCharges } from '../ability/charges/charge-entry.js';
import type { WeaponInventoryMove, WeaponInventoryChange } from './weapon-attack-inventory.js';
import { ecLean } from '../ability/context.js';
import { patchVitalityCluster } from '../compile/held-vitality';
export { patchVitalityCluster } from '../compile/held-vitality';

type Row = Record<string, unknown>;

/** RollType 'damage_roll' — the damage row's roll_type_1. */
export const DAMAGE_ROLL = '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0';
/** Skill 'natural_weapon_skill' — the attack row's roll_type_2 for every natural weapon this slice resolves. */
export const NATURAL_WEAPON_SKILL = '45DE7B42-D548-498A-B7AB-C3DC1C660E57';
/** Stamped on every envelope so a preserved-draw replay (plan §12) can pick the resolver that produced it. */
export const ATTACK_RULES_VERSION = 'a2-s10-2026-09-14';

/** The dice fields one SWING contributes to its log rows — the paid swing (the outcome's own top level) or the rider
 *  (S9). `reactionFired` exists only on the paid swing: the rider never sees a target reaction (plan §20.2). */
export type SwingView = Pick<WeaponAttackOutcome, 'advDisStacks' | 'd20Draws' | 'skillTierDraw' | 'skillTierDie' | 'damageDraws' | 'damageExplosions'
  | 'critical' | 'criticalTuples' | 'criticalBonus' | 'criticalVariant' | 'cascadeTriggers' | 'mitigation' | 'untrained' | 'luckSpent'> & { reactionFired?: string | null; skillRollMod?: number; weaponAttackMod?: number; advDisSources?: WeaponAttackOutcome['advDisSources'];
  /** Wizard v9 damage_type_is_rolled_at_max: the provider's name when this swing's weapon fragment was rolled at max. */
  damageMaxedBy?: string | null; damageDice?: Array<{ value: number; kept: boolean }>; damageAdvDisStacks?: number };

/** One `rolls_full` entry exactly as PHP stores it: [face, label] or [face, label, meta]. The face is a number for a
 *  die / modifier tuple and a SYMBOL string for a mitigation tuple (`0.5 Ω` Resisted, `θ` Negated, `2 λ` Vulnerability —
 *  check_damage_resistance_and_immunity.php). */
export type RollTuple = [number | string, string] | [number | string, string, Record<string, unknown>];

/** The mitigation tuple PHP appends per applied step: [symbol, "<damage type> <effect>", $defense_meta]. */
export const MITIGATION_TUPLE: Record<MitigationKind, { symbol: string; suffix: string }> = {
  resistance: { symbol: '0.5 Ω', suffix: 'Resisted' },
  immunity: { symbol: 'θ', suffix: 'Negated' },
  vulnerability: { symbol: '2 λ', suffix: 'Vulnerability' },
};
export function mitigationTuples(steps: readonly MitigationStep[], damageTypeName: string): RollTuple[] {
  // an IGNORED resistance (the attacker's ignore_damage_type_resistance) is PHP's ρ tuple "<type> Resistance Ignored" (meta kind resistance_ignored)
  return steps.map((step): RollTuple => step.ignored ? ['ρ', `${damageTypeName} Resistance Ignored`, { ...step.meta }]
    : [MITIGATION_TUPLE[step.kind].symbol, `${damageTypeName} ${MITIGATION_TUPLE[step.kind].suffix}`, { ...step.meta }]);
}

export interface AdvDisTraceEntry {
  log_banner?: 'campaign_house_rule';
  delta: number;
  source: string;
  label: string;
  item_id?: string;
  kind?: string;
  origin_name?: string;
  origin_item_id?: string;
}

/** The inputs insert_roll() needs for one row (dice / die / mod / stacks / dc are its roll_meta.dice). */
export interface AttackRollRow {
  roll_type_1: string;
  roll_type_2: string;
  roll_item_id: string;
  dice: number;
  die: number;
  mod: number;
  adv_dis_stacks: number;
  dc: number;
  rolls_full: RollTuple[];
  result: number;
  adv_dis_trace: AdvDisTraceEntry[] | null;
  /** S10: a DAMAGE row's roll_feedback exactly as calc_total_damage_and_apply_to_target writes it (rolled / applied, per type). */
  feedback?: DamageFeedback | null;
}

export interface AttackTruthFrame {
  reaction_members?: Record<string, { rev: number; ec: Row; charges: Array<{ item_id: string; current: number }>; statuses: AttackStatusState;
    position?: { x_pos: number; y_pos: number; z_pos: number };
    playerCharactersChanged?: Array<{ item_id: string; column: string; after: number }> }>;
  status_effects?: Extract<AbilityUseVerdict, { kind: 'resolved' }>;
  damage_status_effects?: Extract<AbilityUseVerdict, { kind: 'resolved' }>;
  form_status_effects?: Extract<AbilityUseVerdict, { kind: 'resolved' }>;
  paid_save_effects?: Extract<AbilityUseVerdict, { kind: 'resolved' }>;
  rider_save_effects?: Extract<AbilityUseVerdict, { kind: 'resolved' }>;
  stray?: { target_id: string; rev: number; reaction: number };
  inventory_state?: Array<{ inventory_id: string; row: Row | null }>;
  inventory_state_rev?: number;
  attacker: { aps: number; aps_used: number | null; weapon_attack: number; head_attack?: 0 | 1; luck?: number; reaction?: 0 | 1; fortunate_status?: Row | null; fortunate_status_rev?: number; charges?: Array<{ item_id: string; current: number }>; statuses?: AttackStatusState };
  target: {
    statuses?: AttackStatusState;
    movement?: MovementTruthChange;
    v_current?: number;
    v_temp?: number;
    reaction?: 0;
    encounter_creature_dead?: 1;
    luck?: number;
    /** The server-rebuilt `computed.vitality` cluster (saved frames only; a provisional carries none). */
    vitality?: Row;
  };
  /** Presentation summary — never read back into a row. */
  outcome: {
    fortunate_fired?: boolean;
    fortunate_pending?: boolean;
    natural: number;
    result: number;
    dc: number;
    hit: boolean;
    critical: boolean;
    damage: number | null;
    reaction_fired: string | null;
    death: WeaponAttackOutcome['death'];
    /** S9: the rider swing's summary, present only when a partner rode (plan §20.4). */
    rider?: { weapon_id: string; label: 'off-hand' | 'main-hand'; natural: number; result: number; hit: boolean; critical: boolean; damage: number | null } | null;
  };
}

/** S9: the rider's log rows — the same shape as the paid swing's, citing the RIDER weapon. */
export interface RiderRolls {
  weapon_id: string;
  label: 'off-hand' | 'main-hand';
  /** `unlucky_roll` row BEFORE the rider's attack: the rider spent the −1 the paid swing's natural 1 left. */
  unlucky: boolean;
  attack: AttackRollRow;
  miss: boolean;
  damage: AttackRollRow | null;
  cascades: AttackRollRow[];
}

export interface WeaponAttackCommand {
  swing_skipped?: true;
  status_effects?: Extract<AbilityUseVerdict, { kind: 'resolved' }>;
  damage_status_effects?: Extract<AbilityUseVerdict, { kind: 'resolved' }>;
  form_status_effects?: Extract<AbilityUseVerdict, { kind: 'resolved' }>;
  paid_save_effects?: Extract<AbilityUseVerdict, { kind: 'resolved' }>;
  rider_save_effects?: Extract<AbilityUseVerdict, { kind: 'resolved' }>;
  skipped_effects?: string[];
  reaction_effects?: Pick<Extract<AbilityUseVerdict, { kind: 'resolved' }>, 'rows' | 'ec' | 'statusesAdded' | 'statusesRemoved' | 'statusStacks' | 'charges' | 'chargesRemoved' | 'playerCharactersChanged' | 'spawnRows' | 'statusTriggerListeners'>;
  reaction_expected_revs?: Record<string, number>;
  /** Originating outbox metadata: offer after the committed luck lands, never a persisted gameplay write. */
  fortunate_offers?: string[];
  teleport_offers?: Extract<AbilityUseVerdict, { kind: 'resolved' }>['teleportOffers'];
  reaction_member_before?: Record<string, Record<string, number | string>>;
  stray?: { target_id: string; expected_rev: number; before_reaction: number; reaction: number; roll: AttackRollRow; unlucky: boolean; reaction_ability_id: string | null; double_natural_one: boolean };
  inventory_move?: WeaponInventoryMove;
  inventory_change?: WeaponInventoryChange;
  ability_id?: string;
  charges?: Array<{ holder: string; item_id: string; before: number; charges: number }>;
  turnTransition?: WeaponAttackDeltas['turnTransition'];
  opportunity_attack?: true;
  movement?: { before: MovementTruthChange; updates: MovementTruthChange; route: Array<[number, number, number]> };
  schema: typeof WEAPON_ATTACK_SCHEMA;
  operation_id: string;
  encounter_id: string;
  actor_id: string;
  expected_rev: number;
  target_id: string;
  target_expected_rev: number;
  weapon_id: string;
  roll_private: number;
  rules_version: string;
  /** Browser-selected placement of the native status log block; absent keeps the historical final-swing slot. */
  status_effects_before_paid?: boolean;
  /** The row scalars the attack was resolved from — the outbox checks `aps`; PHP checks all of them. */
  before: {
    reaction?: number;
    aps: number;
    luck: number;
    head_attack: number;
    weapon_attack: number;
    target: { v_current: number; v_temp?: number; reaction: number; encounter_creature_dead: number; luck?: number };
  };
  /** Every draw the resolver took, in order (the recording source's stream). */
  draws: RecordedDraw[];
  rolls: {
    fortunate: boolean;
    /** `unlucky_roll` log row BEFORE the attack (caster_is_unlucky spent a -1). */
    unlucky: boolean;
    /** The target's reaction that fired: one applied marker by the TARGET before the attack roll. */
    reaction: null | { ability_id: string };
    attack: AttackRollRow | null;
    /** `weapon_attack_missed` marker row after the attack roll. */
    miss: boolean;
    damage: AttackRollRow | null;
    cascades: AttackRollRow[];
    maimed_save: MaimedSave | null;
    maimed_saves?: Array<MaimedSave & { swing: 'paid' | 'rider' }>;
    /** S9: the two-weapon rider's rows, after the paid swing's, under the same roll_id. Absent / null = one swing. */
    rider?: RiderRolls | null;
  };
  writes: {
    attacker: WeaponAttackDeltas['attacker'];
    target: WeaponAttackDeltas['target'];
    death: WeaponAttackOutcome['death'];
    server_follow_up: string[];
    massive_log?: boolean;
  };
  frame: AttackTruthFrame;
  rev?: number;
  target_rev?: number;
}

export interface AttackCommitContext {
  attackRollType?: string;
  abilityId?: string;
  operationId: string;
  encounterId: string;
  actorId: string;
  targetId: string;
  weaponId: string;
  rollPrivate: number;
  rawDiceMod: number;
  rawDiceAdvDisStacks: number;
  /** The attacker's / target's current revisions (the guard and the precondition). */
  attackerRev: number;
  targetRev: number;
  /** The row scalars before the attack (what `before` carries). */
  attackerBefore: { aps: number; aps_total: number; luck: number; head_attack: number; weapon_attack: number; reaction?: number };
  targetBefore: { v_current: number; v_temp?: number; reaction: number; encounter_creature_dead: number; luck?: number };
  /** The weapon's identity for build_damage_dice_meta. */
  weapon: { item_id: string; item_name: string; item_type: string; item_composition?: unknown };
  /** The damage type id + display name (DamageType::getById()['item_name']) for the ` | <name>` label suffix. */
  damageTypeId: string;
  damageTypeName: string;
  /** The attack modifier's essence (skill essence_used) as its VARIABLE name, e.g. 'body'. */
  essenceName: string;
  essenceBonus: number;
  damageEssenceBonus: number;
  weaponDice: number;
  weaponDie: number;
  /** The skill an `untrained` trace entry names: skill_roll_inc.php:239 pushes origin_name = the skill's item_name and
   *  origin_item_id = its catalog id. Optional so older callers / tests keep their shape. */
  skillId?: string;
  skillName?: string;
  /** S9: the RIDER weapon's identity for its rows (build_damage_dice_meta source fields, the ` | <type>` suffix, dice). */
  rider?: { weapon: { item_id: string; item_name: string; item_type: string; item_composition?: unknown }; skillId?: string; skillName?: string; damageTypeId: string; damageTypeName: string; weaponDice: number; weaponDie: number };
  /** S10: DamageType::getById()['item_name'] for an extra fragment's type (the ` | <type>` suffix and `<type> Resisted`). */
  damageTypeNameOf?: (damageTypeId: string) => string;
}

const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
/** PHP ucwords_and_space: underscores to spaces, then each word capitalised. */
const ucwords = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/**
 * The attack roll's `rolls_full`, byte-for-byte the shape skill_roll_inc.php stores (S1 golden, all four
 * variants): the d20 tuples as roll_dice_arr emits them — a flat roll is `d20_1` with `{kept:true}`; an
 * adv/dis roll is one `d20 DIS{n}` / `d20 ADV{n}` tuple per die, each tagged `{roll_order, kept}` and
 * SORTED kept-die-first (ascending for DIS, descending for ADV; the sort is stable so ties keep draw
 * order) — then the trained-tier `Skill Roll d{die}`, then skill_roll_arr_identifiers' tail: `Raw Mod`
 * only when non-zero, and the essence ALWAYS (`Body Essence`). roll_result / sum_mod are shifted off.
 */
export function attackRollsFull(outcome: SwingView, context: Pick<AttackCommitContext, 'rawDiceMod' | 'essenceName' | 'essenceBonus'>): RollTuple[] {
  const tuples: RollTuple[] = [];
  const stacks = outcome.advDisStacks;
  if (stacks === 0) {
    for (const [index, draw] of outcome.d20Draws.entries()) tuples.push([draw.value, `d20_${index + 1}`, { kept: true }]);
  } else {
    const label = `d20 ${stacks > 0 ? 'ADV' : 'DIS'}${Math.abs(stacks)}`;
    const pair = outcome.d20Draws.map((draw, index) => ({ value: draw.value, order: index + 1 }));
    pair.sort((a, b) => (stacks > 0 ? b.value - a.value : a.value - b.value));
    pair.forEach((draw, index) => tuples.push([draw.value, label, { roll_order: draw.order, kept: index === 0 }]));
  }
  if (outcome.skillTierDraw !== null && outcome.skillTierDie) tuples.push([outcome.skillTierDraw, `Skill Roll d${outcome.skillTierDie}`]);
  tuples.push(...applyRawDiceModifiers(context).faces);
  if (outcome.skillRollMod) tuples.push([outcome.skillRollMod, 'Skill Roll Mod']);
  if (outcome.weaponAttackMod) tuples.push([outcome.weaponAttackMod, 'Weapon Attack Mod']);
  tuples.push([context.essenceBonus, `${ucwords(context.essenceName)} Essence`]);
  return tuples;
}

/** build_damage_dice_meta's base for a weapon fragment (source fields in PHP's key order). */
function damageMetaBase(context: Pick<AttackCommitContext, 'damageTypeId' | 'weapon'>): Record<string, unknown> {
  return {
    damage_type_id: context.damageTypeId,
    source_item_id: context.weapon.item_id,
    source_name: context.weapon.item_name,
    source_item_type: context.weapon.item_type,
    source_kind: 'weapon',
    ...(context.weapon.item_composition ? { item_composition: context.weapon.item_composition } : {}),
  };
}

/**
 * The damage roll's `rolls_full` as damage_type_roll.php stores it: the base dice `d{die}_{x}` (x = draw
 * index) rsort-ed by face (ties by label, descending), each `{kept:true} + fragment meta` and suffixed
 * ` | <damage type>`; then the explosion links, the crit tuples (Rolled: one per die; Maximum / Doubled:
 * one `Critical Damage` summary), and the essence tuple `body Essence` (the raw variable name, lower case)
 * with `kind: 'essence'`. Neither the explosion / crit tuples nor the essence carry the type suffix.
 */
export function damageRollsFull(outcome: SwingView, context: Pick<AttackCommitContext, 'damageTypeId' | 'damageTypeName' | 'weapon' | 'essenceName' | 'damageEssenceBonus' | 'weaponDie'>): RollTuple[] {
  const base = damageMetaBase(context);
  const die = context.weaponDie;
  const dice = (outcome.damageDice ?? outcome.damageDraws.map(value => ({ value, kept: true })))
    .map((draw, index) => ({ ...draw, label: `d${die}_${index + 1}${outcome.damageAdvDisStacks ? ' ADV' + outcome.damageAdvDisStacks : ''}` }));
  // PHP rsort() on 2-element [value, label] arrays: value desc, then label desc.
  dice.sort((a, b) => b.value - a.value || (a.label < b.label ? 1 : a.label > b.label ? -1 : 0));
  const tuples: RollTuple[] = dice.map(d => [d.value, `${d.label} | ${context.damageTypeName}`, { kept: d.kept, ...base }]);
  for (const link of outcome.damageExplosions) {
    tuples.push([link.value, `d${die} EXPLODE ${link.link} (needed ${link.threshold}+)`, {
      ...base, kind: 'explosion', explosion_link: link.link, explosion_threshold: link.threshold, explosion_die: die,
    }]);
  }
  // damage_type_is_rolled_at_max (Wizard v9): damage_type_roll's forced-extreme tuple, after the dice / explosions, before the crit
  if (outcome.damageMaxedBy != null) tuples.push(['Ω', `Damage Type is Rolled At Max — ${outcome.damageMaxedBy}`, base]);
  if (outcome.critical) {
    if (outcome.criticalTuples.length > 0) {
      for (const t of outcome.criticalTuples) {
        tuples.push([t.value, t.label, t.kind === 'explosion'
          ? { ...base, kind: 'explosion', explosion_die: die }
          : { ...base, kind: 'critical' }]);
      }
    } else {
      tuples.push([outcome.criticalBonus, outcome.criticalVariant === 'doubled' ? 'Critical Damage (Doubled)' : 'Critical Damage', { ...base, kind: 'critical' }]);
    }
  }
  for (const trigger of outcome.cascadeTriggers ?? []) {
    if (trigger.bonus !== 0) tuples.push([trigger.bonus, CRIT_CASCADE_RULES[trigger.rule].bonusLabel, { ...base, kind: 'critical' }]);
  }
  if (context.damageEssenceBonus !== 0) {
    tuples.push([context.damageEssenceBonus, `${context.essenceName} Essence`, { ...base, kind: 'essence', essence_name: context.essenceName }]);
  }
  // S6: the target's damage-type mitigation, appended AFTER the essence (check_damage_resistance_and_immunity runs at
  // damage_type_roll.php:1033, after the essence tuple at ~1010), one tuple per applied step in PHP's order. The face is
  // the SYMBOL, the label is `<damage type> <effect>`, the meta is $defense_meta — none of them carry the fragment base.
  tuples.push(...mitigationTuples(outcome.mitigation ?? [], context.damageTypeName));
  return tuples;
}

/**
 * S10: an extra fragment's tuples in damage_type_roll.php's order — the dice (rsorted, `d{die}_{x} | <type>`, `{kept} + meta`), the
 * explosion links, the crit bonus (per die for Rolled, one summary otherwise), the `mod` tuple (named after the source), then its own
 * mitigation. No essence: Body only rides a weapon fragment (damage_type_roll_applies_weapon_body_mod_to_roll).
 */
export function extraFragmentRollsFull(fragment: ExtraDamageFragment, damageTypeName: string): RollTuple[] {
  const base = fragment.metaBase;
  const die = fragment.die;
  const dice = fragment.draws.map((value, index) => ({ value, label: `d${die}_${index + 1}` }));
  dice.sort((a, b) => b.value - a.value || (a.label < b.label ? 1 : a.label > b.label ? -1 : 0));
  const tuples: RollTuple[] = dice.map(d => [d.value, `${d.label} | ${damageTypeName}`, { kept: true, ...base }]);
  for (const link of fragment.explosions) {
    tuples.push([link.value, `d${die} EXPLODE ${link.link} (needed ${link.threshold}+)`, {
      ...base, kind: 'explosion', explosion_link: link.link, explosion_threshold: link.threshold, explosion_die: die,
    }]);
  }
  // damage_type_is_rolled_at_max (Wizard v9): damage_type_roll's forced-extreme tuple, after the dice / explosions, before the crit
  if (fragment.maxedBy != null) tuples.push(['Ω', `Damage Type is Rolled At Max — ${fragment.maxedBy}`, base]);
  if (fragment.criticalVariant !== null) {
    if (fragment.criticalTuples.length > 0) {
      for (const t of fragment.criticalTuples) tuples.push([t.value, t.label, t.kind === 'explosion' ? { ...base, kind: 'explosion', explosion_die: die } : { ...base, kind: 'critical' }]);
    } else {
      tuples.push([fragment.criticalBonus, fragment.criticalVariant === 'doubled' ? 'Critical Damage (Doubled)' : 'Critical Damage', { ...base, kind: 'critical' }]);
    }
  }
  if (fragment.mod !== 0) tuples.push([fragment.mod, fragment.sourceName || 'Damage Mod', { ...base, kind: 'damage_mod' }]);
  tuples.push(...mitigationTuples(fragment.mitigation, damageTypeName));
  // check_post_damage_type_roll_conditions: each failing gate stamps every tuple not yet stamped (gate_roll_tokens.php — first reason
  // wins, explanation tuples skipped), then appends its explanation tuple; the fragment already reads 0.
  for (const gate of fragment.gates ?? []) {
    for (const tuple of tuples) {
      const meta = (tuple[2] ?? {}) as Record<string, unknown>;
      if (meta.ft_gate_explanation || meta.ft_gate_not_applied) continue;
      (tuple as unknown[])[2] = { ...meta, ft_gate_not_applied: true, ft_gate_reason_code: gate.reason };
    }
    tuples.push([gate.face, gate.label, { ...gate.meta }]);
  }
  return tuples;
}

/** Named status reductions use integer faces in A2's existing numeric-tuple transport. */
export function adjustmentRollsFull(adjustments: readonly DamageAdjustment[]): RollTuple[] {
  return adjustments.map(a => [/^-\d+$/.test(a.face) && a.label !== 'Damage Mitigation' ? Number(a.face) : a.face, a.label]);
}

/** The attack row's adv/dis trace, in the order skill_roll_inc.php pushes the entries. */
export function advDisTraceOf(outcome: SwingView, rawStacks: number, skill?: { id?: string; name?: string }): AdvDisTraceEntry[] | null {
  const trace: AdvDisTraceEntry[] = [];
  // The UNTRAINED disadvantage is pushed while the skill roll is BUILT (skill_roll_inc.php:239) — before the sidebar
  // modifier (272), the target's reaction (811) and the luck spend (1025). Key order is PHP's adv_dis_trace_push.
  if (outcome.untrained) {
    trace.push({ delta: -1, source: 'untrained', label: 'Untrained', kind: 'skill_training',
      ...(skill?.name ? { origin_name: skill.name } : {}), ...(skill?.id ? { origin_item_id: skill.id } : {}) });
  }
  for (const source of outcome.advDisSources ?? []) if (!['skill_training', 'manual', 'reaction', 'luck'].includes(source.kind))
    trace.push({ delta: source.stacks, source: source.kind === 'environment' && source.label === 'Wall of Water'
      ? 'painted_wall_water' : source.kind, label: source.label, kind: source.kind });
  if (rawStacks !== 0) trace.push({ delta: rawStacks, source: 'raw_dice', label: 'Manual', kind: 'manual' }); // skill_roll_inc.php:272 (Codex S6b F1: the old strings were invented)
  if (outcome.reactionFired) {
    trace.push({ delta: -1, source: 'target_trigger_roll_type_against_dis', label: 'Evade', item_id: outcome.reactionFired, kind: 'status_effect', origin_name: 'On Target' });
  }
  if (outcome.luckSpent) trace.push({ delta: -1, source: 'unlucky', label: 'Unlucky', kind: 'luck' });
  return trace.length ? trace : null;
}

export function cascadeRollRow(trigger: CritCascadeTrigger): AttackRollRow {
  const rule = CRIT_CASCADE_RULES[trigger.rule];
  return {
    roll_type_1: rule.rollType, roll_type_2: '', roll_item_id: rule.id,
    dice: 1, die: 20, mod: 0, adv_dis_stacks: 0, dc: 20,
    rolls_full: [[trigger.face, 'd20_1']], result: trigger.face,
    adv_dis_trace: [{ log_banner: 'campaign_house_rule', delta: 0, source: 'campaign_house_rule', label: rule.name,
      kind: 'campaign_house_rule', origin_item_id: rule.id }],
  };
}

export function buildWeaponAttackCommand(
  outcome: WeaponAttackOutcome,
  deltas: WeaponAttackDeltas,
  draws: RecordedDraw[],
  context: AttackCommitContext,
): WeaponAttackCommand {
  const apsUsed = Math.max(0, context.attackerBefore.aps_total - deltas.attacker.aps);
  // S9: the rider (plan §20.4). Its rows cite the RIDER weapon, its damage row carries NO essence (mod 0), and it
  // never sees a target reaction. The builder requires the rider context whenever the resolver produced a rider.
  const riderOutcome: RiderSwingOutcome | null = outcome.rider ?? null;
  if (riderOutcome && !context.rider) throw new Error('weapon-attack-commit: the resolver produced a rider swing but no rider context was supplied');
  const riderContext = riderOutcome && context.rider ? context.rider : null;
  const paidSaveEffects = maimedAscensionEffects(outcome.maimedSave, upper(context.targetId));
  const riderSaveEffects = maimedAscensionEffects(riderOutcome?.maimedSave ?? null, upper(context.targetId));
  const postedSave = (save: MaimedSave): MaimedSave => {
    const { ascension, ...posted } = save;
    return { ...posted, ascensionFace: ascension ? null : posted.ascensionFace };
  };
  const frame: AttackTruthFrame = {
    attacker: { aps: deltas.attacker.aps, aps_used: apsUsed > 0 ? apsUsed : null, weapon_attack: deltas.attacker.weapon_attack,
      ...(deltas.attacker.head_attack !== undefined ? { head_attack: 1 as const } : {}), ...(deltas.attacker.luck !== undefined ? { luck: deltas.attacker.luck } : {}), ...(deltas.attacker.reaction !== undefined ? { reaction: deltas.attacker.reaction } : {}) },
    target: { ...deltas.target },
    outcome: {
      fortunate_fired: outcome.fortunateFired ?? false,
      fortunate_pending: (outcome.fortunateFired ?? false) && !(outcome.interSwingFortunate && outcome.rider),
      natural: outcome.naturalD20, result: outcome.result, dc: outcome.dc, hit: outcome.hit, critical: outcome.critical,
      damage: outcome.damage, reaction_fired: outcome.reactionFired, death: outcome.death,
      ...(riderOutcome ? {
        rider: { weapon_id: upper(riderOutcome.weaponId), label: riderOutcome.label, natural: riderOutcome.naturalD20, result: riderOutcome.result, hit: riderOutcome.hit, critical: riderOutcome.critical, damage: riderOutcome.damage },
      } : {}),
    },
  };
  const riderRolls: RiderRolls | null = riderOutcome && riderContext ? {
    weapon_id: upper(riderOutcome.weaponId),
    label: riderOutcome.label,
    unlucky: riderOutcome.luckSpent,
    attack: {
      roll_type_1: riderOutcome.rollType ?? WEAPON_MELEE_ATTACK_ROLL, roll_type_2: riderContext.skillId ?? NATURAL_WEAPON_SKILL, roll_item_id: upper(riderOutcome.weaponId),
      dice: 1, die: 20, mod: riderOutcome.modifier, adv_dis_stacks: riderOutcome.advDisStacks, dc: riderOutcome.dc,
      rolls_full: attackRollsFull(riderOutcome, context), result: riderOutcome.result,
      adv_dis_trace: advDisTraceOf(riderOutcome, context.rawDiceAdvDisStacks, { id: riderContext.skillId ?? NATURAL_WEAPON_SKILL, name: riderContext.skillName ?? context.skillName }),
    },
    miss: !riderOutcome.hit,
    damage: riderOutcome.hit && riderOutcome.damage !== null ? {
      roll_type_1: DAMAGE_ROLL, roll_type_2: upper(riderContext.damageTypeId), roll_item_id: upper(riderOutcome.weaponId),
      dice: riderContext.weaponDice, die: riderContext.weaponDie, mod: 0, adv_dis_stacks: riderOutcome.damageAdvDisStacks ?? 0, dc: 0,
      // No Body on the rider's damage (damage_type_roll_applies_weapon_body_mod_to_roll → false): essence 0 emits no tuple.
      rolls_full: [
        ...damageRollsFull(riderOutcome, { damageTypeId: riderContext.damageTypeId, damageTypeName: riderContext.damageTypeName, weapon: riderContext.weapon, essenceName: context.essenceName, damageEssenceBonus: 0, weaponDie: riderContext.weaponDie }),
        ...(riderOutcome.extraDamage ?? []).flatMap(f => extraFragmentRollsFull(f, context.damageTypeNameOf?.(f.damageTypeId) ?? 'Unknown')),
        ...adjustmentRollsFull(riderOutcome.damageAdjustments ?? []),
      ],
      result: riderOutcome.damage, adv_dis_trace: null,
      ...(riderOutcome.damageFeedback ? { feedback: riderOutcome.damageFeedback } : {}),
    } : null,
    cascades: (riderOutcome.cascadeTriggers ?? []).map(cascadeRollRow),
  } : null;
  const attack: AttackRollRow = {
    roll_type_1: context.attackRollType ?? WEAPON_MELEE_ATTACK_ROLL,
    roll_type_2: context.skillId ?? NATURAL_WEAPON_SKILL,
    roll_item_id: upper(context.abilityId ?? context.weaponId),
    dice: 1,
    die: 20,
    mod: outcome.modifier,
    adv_dis_stacks: outcome.advDisStacks,
    dc: outcome.dc,
    rolls_full: attackRollsFull(outcome, context),
    result: outcome.result,
    adv_dis_trace: advDisTraceOf(outcome, context.rawDiceAdvDisStacks, { id: context.skillId, name: context.skillName }),
  };
  const damage: AttackRollRow | null = outcome.hit && outcome.damage !== null ? {
    roll_type_1: DAMAGE_ROLL,
    roll_type_2: upper(context.damageTypeId),
    roll_item_id: upper(context.weaponId),
    dice: context.weaponDice,
    die: context.weaponDie,
    mod: context.damageEssenceBonus,
    adv_dis_stacks: outcome.damageAdvDisStacks ?? 0,
    dc: 0,
    rolls_full: [
      ...damageRollsFull(outcome, context),
      ...(outcome.extraDamage ?? []).flatMap(f => extraFragmentRollsFull(f, context.damageTypeNameOf?.(f.damageTypeId) ?? 'Unknown')),
      ...adjustmentRollsFull(outcome.damageAdjustments ?? []),
    ],
    result: outcome.damage,
    adv_dis_trace: null,
    ...(outcome.damageFeedback ? { feedback: outcome.damageFeedback } : {}),
  } : null;
  return {
    schema: WEAPON_ATTACK_SCHEMA,
    ...(outcome.skippedEffects?.length ? { skipped_effects: outcome.skippedEffects } : {}),
    ...(outcome.swingSkipped ? { swing_skipped: true as const } : {}),
    ...(deltas.turnTransition ? { turnTransition: deltas.turnTransition } : {}),
    ...(outcome.opportunityAttack ? { opportunity_attack: true as const } : {}),
    operation_id: context.operationId,
    encounter_id: upper(context.encounterId),
    actor_id: upper(context.actorId),
    expected_rev: context.attackerRev,
    target_id: upper(context.targetId),
    target_expected_rev: context.targetRev,
    weapon_id: upper(context.weaponId),
    ...(context.abilityId ? { ability_id: upper(context.abilityId) } : {}),
    roll_private: [0, 1, 2].includes(context.rollPrivate) ? context.rollPrivate : 0,
    rules_version: ATTACK_RULES_VERSION,
    ...(paidSaveEffects ? { paid_save_effects: paidSaveEffects } : {}),
    ...(riderSaveEffects ? { rider_save_effects: riderSaveEffects } : {}),
    before: {
      ...(context.attackerBefore.reaction !== undefined ? { reaction: context.attackerBefore.reaction } : {}),
      aps: context.attackerBefore.aps, luck: context.attackerBefore.luck,
      head_attack: context.attackerBefore.head_attack, weapon_attack: context.attackerBefore.weapon_attack,
      target: { ...context.targetBefore },
    },
    draws: draws.map(d => ({ ...d })),
    rolls: {
      fortunate: outcome.fortunateFired ?? false,
      unlucky: outcome.luckSpent,
      reaction: outcome.reactionFired ? { ability_id: outcome.reactionFired === EVADE_ID ? EVADE_ID : outcome.reactionFired } : null,
      attack: outcome.swingSkipped ? null : attack,
      miss: !outcome.swingSkipped && !outcome.hit,
      damage,
      cascades: (outcome.cascadeTriggers ?? []).map(cascadeRollRow),
      maimed_save: outcome.maimedSave ? postedSave(outcome.maimedSave) : null,
      ...(riderOutcome?.maimedSave ? { maimed_saves: [
        ...(outcome.maimedSave ? [{ ...postedSave(outcome.maimedSave), swing: 'paid' as const }] : []),
        { ...postedSave(riderOutcome.maimedSave), swing: 'rider' as const },
      ] } : {}),
      ...(riderRolls ? { rider: riderRolls } : {}),
    },
    writes: {
      attacker: { ...deltas.attacker },
      target: { ...deltas.target },
      death: outcome.death,
      server_follow_up: [...deltas.serverFollowUp],
      ...(deltas.massiveLog !== undefined ? { massive_log: deltas.massiveLog } : {}),
    },
    frame,
  };
}

// ---------------------------------------------------------------------------------------------------
// Applying a frame to the two rows the browser holds. Keeps row + bucket identity (the encounter store
// and the caster cache share them), mirrors the lean rules of build_encounter_creature_arr (a 0 is
// elided), and settles the target's vitality numbers and tier together.
// ---------------------------------------------------------------------------------------------------
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;

function setLean(ec: Row, key: string, value: number | undefined): void {
  if (value === undefined) return;
  if (value === 0) delete ec[key]; else ec[key] = value;
}

export function applyAttackTruth(attacker: Row, target: Row | null, frame: AttackTruthFrame, revs?: { attacker?: number; target?: number }): void {
  if (frame.attacker.statuses) attacker.statuses = mergeAttackStatuses(attacker, frame.attacker.statuses);
  for (const charge of frame.attacker.charges ?? []) setRowAbilityCharges(attacker, charge.item_id, charge.current);
  const aec = (attacker.encounter_creatures ??= {}) as Row;
  applyApsScalar(aec, frame.attacker.aps, frame.attacker.aps_used ?? null);
  setLean(aec, 'weapon_attack', frame.attacker.weapon_attack);
  if (frame.attacker.head_attack !== undefined) setLean(aec, 'head_attack', frame.attacker.head_attack);
  if (frame.attacker.luck !== undefined) setLean(aec, 'luck', frame.attacker.luck);
  if (frame.attacker.reaction !== undefined) setLean(aec, 'reaction', frame.attacker.reaction);
  if (frame.attacker.fortunate_status !== undefined) {
    const statuses = (attacker.statuses ??= {}) as Row;
    const entries = Array.isArray(statuses.target) ? statuses.target as Row[] : [];
    statuses.target = entries.filter(s => !isFortunateStatus(s));
    if (frame.attacker.fortunate_status !== null) (statuses.target as Row[]).unshift({ ...frame.attacker.fortunate_status }); // a NEW status first (N4e)
  }
  if (Number.isInteger(revs?.attacker) && revs!.attacker! >= 0) aec.movement_rev = revs!.attacker;
  if (!target) return;
  const tec = (target.encounter_creatures ??= {}) as Row;
  if (frame.target.statuses) target.statuses = mergeAttackStatuses(target, frame.target.statuses);
  if (frame.target.movement) applyMovementTruth(target as MovementTruthRow, frame.target.movement);
  if (frame.target.reaction !== undefined) setLean(tec, 'reaction', frame.target.reaction);
  if (frame.target.v_temp !== undefined) {
    setLean(tec, 'v_temp', frame.target.v_temp);
    const vitality = asRow(asRow(target.computed)?.vitality);
    if (vitality) vitality.v_temp = frame.target.v_temp;
  }
  if (frame.target.luck !== undefined) setLean(tec, 'luck', frame.target.luck);
  if (frame.target.encounter_creature_dead !== undefined) setLean(tec, 'encounter_creature_dead', frame.target.encounter_creature_dead);
  if (frame.target.v_current !== undefined) {
    setLean(tec, 'v_current', frame.target.v_current);
    if (frame.target.vitality) {
      const computed = (target.computed ??= {}) as Row;
      computed.vitality = JSON.parse(JSON.stringify(frame.target.vitality));
    } else {
      patchVitalityCluster(target, frame.target.v_current);
    }
  }
  if (Number.isInteger(revs?.target) && revs!.target! >= 0) tec.movement_rev = revs!.target;
}

export type AttackTargetField = 'v_current' | 'v_temp' | 'reaction' | 'encounter_creature_dead' | 'luck' | 'movement';
